export type OidcRuntimeConfig = {
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
};

export type OidcConfigResult =
  | { ok: true; config: OidcRuntimeConfig }
  | { ok: false; missing: string[] };

const DEFAULT_SCOPE = "openid profile email";

function cleanEnvValue(value: string | undefined): string {
  return value?.trim() ?? "";
}

export async function fetchRuntimeConfig(): Promise<OidcRuntimeConfig | null> {
  try {
    const response = await fetch("/config.json");
    if (!response.ok) return null;

    const json = await response.json();
    const authority = cleanEnvValue(json.authority);
    if (!authority) return null;

    return {
      authority,
      clientId: cleanEnvValue(json.clientId),
      redirectUri: cleanEnvValue(json.redirectUri),
      postLogoutRedirectUri: cleanEnvValue(json.postLogoutRedirectUri),
      scope: cleanEnvValue(json.scope),
    };
  } catch {
    return null;
  }
}

export function readOidcRuntimeConfig(
  runtimeOverrides?: OidcRuntimeConfig | null
): OidcConfigResult {
  const config = {
    authority:
      cleanEnvValue(runtimeOverrides?.authority) ||
      cleanEnvValue(import.meta.env.OIDC_AUTHORITY),
    clientId:
      cleanEnvValue(runtimeOverrides?.clientId) ||
      cleanEnvValue(import.meta.env.OIDC_CLIENT_ID),
    redirectUri:
      cleanEnvValue(runtimeOverrides?.redirectUri) ||
      cleanEnvValue(import.meta.env.OIDC_REDIRECT_URI),
    postLogoutRedirectUri:
      cleanEnvValue(runtimeOverrides?.postLogoutRedirectUri) ||
      cleanEnvValue(import.meta.env.OIDC_POST_LOGOUT_REDIRECT_URI),
    scope:
      cleanEnvValue(runtimeOverrides?.scope) ||
      cleanEnvValue(import.meta.env.OIDC_SCOPE) ||
      DEFAULT_SCOPE,
  };

  const missing = [
    ["OIDC_AUTHORITY", config.authority],
    ["OIDC_CLIENT_ID", config.clientId],
    ["OIDC_REDIRECT_URI", config.redirectUri],
    ["OIDC_POST_LOGOUT_REDIRECT_URI", config.postLogoutRedirectUri],
  ]
    .filter(([, value]) => !value)
    .map(([name]) => name);

  if (missing.length > 0) {
    return { ok: false, missing };
  }

  return { ok: true, config };
}
